import { Card, CardContent } from '@/components/ui-kit/card';
import { Button } from '@/components/ui-kit/button';
import { ConfigParamsModal } from '@/modules/gpt-chats/components/tools/mcp-server-details-modals/config-params-modal';
import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { ScrollArea, ScrollBar } from '@/components/ui-kit/scroll-area';
import { Pagination } from '@/components/ui-kit/pagination';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { FilterToolbar, FilterChangeHandler } from '@/components/filter-toolbar';
import { useGetToolDetails, useRefreshMCPServer } from '@/modules/gpt-chats/hooks/use-tools';
import { Eye, Settings } from 'lucide-react';

import { ActionDetailsModal } from '@/modules/gpt-chats/components/tools/mcp-server-details-modals/action-details-modal';
import { IAction } from '@/modules/gpt-chats/types/tools.service.type';
import { showSuccessToast, showErrorToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useSearchParams } from '@/lib/react-router-compat';
import { useTranslation } from 'react-i18next';

interface ActionEntry {
  actionId: string;
  name: string;
  description: string;
  action: IAction;
}

const useMcpConfigFilterQueryParams = () => {
  const [queryParams, setQueryParams] = useQueryStates({
    pageNumber: parseAsInteger.withDefault(0),
    pageSize: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(''),
  });
  return { queryParams, setQueryParams };
};

interface McpServerConfigurationProps {
  mcpServerId: string;
}

export const McpServerConfiguration = ({ mcpServerId }: McpServerConfigurationProps) => {
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const { queryParams, setQueryParams } = useMcpConfigFilterQueryParams();
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IAction | null>(null);
  const [isActionDetailsOpen, setIsActionDetailsOpen] = useState(false);
  const { data: toolDetails, isLoading } = useGetToolDetails(mcpServerId, isBlocksTool);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutateAsync: refreshMCPServer, isPending: isRefreshing } = useRefreshMCPServer();

  const allData: ActionEntry[] = useMemo(() => {
    if (!toolDetails?.MCPConfig?.Actions) return [];

    return Object.entries(toolDetails.MCPConfig.Actions).map(([actionId, action]) => ({
      actionId: actionId,
      name: action.Name,
      description: action.Description,
      action: action,
    }));
  }, [toolDetails]);

  const filteredData = useMemo(() => {
    let filtered = [...allData];

    if (queryParams.search) {
      const searchLower = queryParams.search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.description.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allData, queryParams.search]);

  const pageSize = 10;
  const totalCount = filteredData.length;

  const paginatedData = useMemo(() => {
    const startIndex = queryParams.pageNumber * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, queryParams.pageNumber, pageSize]);

  const onChange: FilterChangeHandler<{ search: string }> = (key, value) => {
    setQueryParams((prev) => ({
      ...prev,
      [key]: value,
      pageNumber: 0,
    }));
  };

  const onReset = () => {
    setQueryParams({ search: '', pageNumber: 0, pageSize: 10 });
  };

  const handleRefreshMCP = async () => {
    try {
      showSuccessToast({
        title: t('REFRESHING'),
        description: t('UPDATING_MCP_DATA'),
      });

      const result = await refreshMCPServer({
        toolId: mcpServerId,
        isBlocksTool,
      });
      showSuccessToast({
        title: t('REFRESH_SUCCESSFUL'),
        description: result.detail || t('MCP_SERVER_REFRESHED_SUCCESSFULLY'),
      });
      queryClient.invalidateQueries({ queryKey: ['tools', 'details', mcpServerId] });
    } catch (error) {
      console.error('Failed to refresh MCP server:', error);
      showErrorToast({
        title: t('REFRESH_FAILED'),
        errors: error,
      });
    }
  };

  const columns = useMemo<ColumnDef<ActionEntry>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('PARAM_NAME'),
        cell: ({ row }) => <span className="font-medium">{row.getValue('name')}</span>,
      },
      {
        accessorKey: 'description',
        header: t('DESCRIPTION'),
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.getValue('description')}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedAction(row.original.action);
              setIsActionDetailsOpen(true);
            }}
            className="h-8 gap-2"
          >
            <Eye className="h-4 w-4" />
            {t('VIEW')}
          </Button>
        ),
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isMCPConfigEmpty = useMemo(() => {
    if (!toolDetails?.MCPConfig) return true;
    const config = toolDetails.MCPConfig;
    return !config.ServerURL || config.ServerURL.trim() === '';
  }, [toolDetails]);

  if (isLoading) {
    return (
      <Card className="flex flex-col gap-2 rounded shadow-none">
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[100px] w-full" />
      </Card>
    );
  }

  if (isMCPConfigEmpty) {
    return (
      <Card className="rounded shadow-none">
        <CardContent className="flex min-h-[400px] flex-col items-center justify-center p-12">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <Settings className="h-10 w-10 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">{t('NO_CONFIGURATION_FOUND')}</h3>
              <p className="text-sm text-muted-foreground">{t('CONFIGURE_MCP_DESCRIPTION')}</p>
            </div>
            <Button onClick={() => setIsConfigModalOpen(true)} size="lg">
              {t('CONFIG_PARAMS')}
            </Button>
          </div>
          <ConfigParamsModal
            open={isConfigModalOpen}
            onOpenChange={setIsConfigModalOpen}
            name="Configuration"
            toolId={mcpServerId}
            mcpConfig={toolDetails?.MCPConfig}
            isBlocksTool={isBlocksTool}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded shadow-none">
      <CardContent className="p-0">
        <div className="flex items-center justify-between gap-4 p-4">
          <FilterToolbar
            filters={[{ key: 'search', type: 'SearchInput', label: 'Search' }]}
            values={{
              search: queryParams.search,
            }}
            defaultValues={{ search: '' }}
            onChange={onChange}
            onReset={onReset}
          />
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefreshMCP} disabled={isRefreshing}>
              {t('UPDATE_API')}
            </Button>
            <Button onClick={() => setIsConfigModalOpen(true)}>Config Params</Button>
          </div>
        </div>

        <ScrollArea className="w-full">
          <Table className="text-sm">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="font-medium text-muted-foreground">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer font-normal"
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t('NO_ACTIONS_FOUND')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <div className="flex items-center justify-between border-t p-4">
          <div className="text-sm text-muted-foreground">
            Showing {queryParams.pageNumber * pageSize + 1}-
            {Math.min((queryParams.pageNumber + 1) * pageSize, totalCount)} {t('OF')} {totalCount}{' '}
            {t('ENTRIES')}
          </div>
          <Pagination
            page={queryParams.pageNumber}
            pageSize={pageSize}
            totalCount={totalCount}
            pageSizeOptions={[10]}
            onChange={(newPage) => setQueryParams({ pageNumber: newPage })}
          />
        </div>
      </CardContent>

      <ConfigParamsModal
        open={isConfigModalOpen}
        onOpenChange={setIsConfigModalOpen}
        name="Configuration"
        toolId={mcpServerId}
        mcpConfig={toolDetails?.MCPConfig}
        isBlocksTool={isBlocksTool}
      />

      <ActionDetailsModal
        open={isActionDetailsOpen}
        onOpenChange={setIsActionDetailsOpen}
        action={selectedAction}
        isBlocksTool={isBlocksTool}
      />
    </Card>
  );
};
